import {useMemo} from 'react';
import {Alert, Collapse, Tooltip} from 'antd';
import {FilterField} from './FilterField';
import {
  FILTER_PANEL_HELP,
  featureLabel,
  humanizeField,
  missingRequiredFields,
} from './constants';
import styles from './themes.module.scss';

/** Mechanical result-shaping rather than "what is in this theme". */
const PAGING = ['page', 'limit', 'paginate'];
const isPaging = field => PAGING.indexOf(field.name) !== -1;

const isToggle = field => field.type === 'Boolean' && !field.enumValues?.length;

/**
 * Splits a branch's fields into the four things an admin is actually doing.
 *
 * Derived from the metadata — `required`, `type`, and the three paging names —
 * never from a hard-coded field list, so a field the backend adds lands in the
 * right section on its own.
 */
const sectionsFor = fields => {
  const required = fields.filter(field => field.required);
  const rest = fields.filter(field => !field.required);
  return {
    required,
    filters: rest.filter(field => !isPaging(field) && !isToggle(field)),
    toggles: rest.filter(field => !isPaging(field) && isToggle(field)),
    paging: rest.filter(isPaging),
  };
};

/**
 * How many fields the admin has actually chosen.
 *
 * A metadata `defaultValue` is seeded into every new branch, so counting "has
 * a value" would mark six collection fields as set before anyone touched the
 * form — and auto-open the sections meant to start closed. Only a value that
 * differs from the default counts.
 */
const countSet = (fields, values = {}) =>
  fields.filter(field => {
    const value = values?.[field.name];
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return (
      value !== field.defaultValue &&
      String(value) !== String(field.defaultValue)
    );
  }).length;

/**
 * One metadata-driven panel for a selected feature's branch.
 *
 * A financial-instrument branch is 32 fields and a collection branch 17. As
 * one flat three-column grid that read as a wall, with eleven toggles each
 * taking a full cell for a 44px switch. Required fields now lead, the toggles
 * sit in a dense grid of their own, and the rarely-touched sections start
 * collapsed — open already when they carry a value, so nothing hides silently.
 *
 * `structure` is the routing contract + `fields[]` for this feature. `values` is
 * the branch's current form object; `onChange(name, value)` updates one field.
 */
export const FilterPanel = ({structure, values, onChange, disabled}) => {
  const sections = useMemo(
    () => sectionsFor(structure?.fields || []),
    [structure],
  );

  if (!structure) return null;
  const missing = missingRequiredFields(structure.fields, values);

  /**
   * The instrument type lives inside one asset class, so a type chosen under
   * the previous class would otherwise survive the switch and be submitted as
   * a pair the gateway has no instruments for.
   */
  const handleChange = (name, next) => {
    onChange(name, next);
    if (name === 'assetClassKey' && values?.instrumentKey) {
      onChange('instrumentKey', undefined);
    }
  };

  const render = (field, compact) => (
    <FilterField
      key={field.name}
      field={field}
      feature={structure.feature}
      branch={values}
      compact={compact}
      disabled={disabled}
      value={values?.[field.name]}
      onChange={next => handleChange(field.name, next)}
    />
  );

  const togglesSet = countSet(sections.toggles, values);
  const pagingSet = countSet(sections.paging, values);

  const collapsible = [
    sections.toggles.length && {
      key: 'toggles',
      label: `Toggles${togglesSet ? ` · ${togglesSet} on` : ''}`,
      body: (
        <div className={styles.toggleGrid}>
          {sections.toggles.map(field => render(field, true))}
        </div>
      ),
      set: togglesSet,
    },
    sections.paging.length && {
      key: 'paging',
      label: `Result size${pagingSet ? ` · ${pagingSet} set` : ''}`,
      body: (
        <div className={styles.fieldGrid}>
          {sections.paging.map(field => render(field, false))}
        </div>
      ),
      set: pagingSet,
    },
  ].filter(Boolean);

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <h3>
          {featureLabel(structure.feature)} filter
          <Tooltip title={FILTER_PANEL_HELP}>
            <span className={styles.hintMark}>?</span>
          </Tooltip>
        </h3>
        <span className={styles.mutedText}>
          Feeds <code>{structure.query}</code> ({structure.argument})
        </span>
      </div>

      {missing.length > 0 && (
        <Alert
          className={styles.alert}
          type="warning"
          showIcon
          message={`Still required: ${missing.map(humanizeField).join(', ')}`}
        />
      )}

      {sections.required.length > 0 && (
        <div className={styles.subSection}>
          <div className={styles.subHead}>Required</div>
          <div className={styles.fieldGrid}>
            {sections.required.map(field => render(field, false))}
          </div>
        </div>
      )}

      {sections.filters.length > 0 && (
        <div className={styles.subSection}>
          <div className={styles.subHead}>Filters</div>
          <div className={styles.fieldGrid}>
            {sections.filters.map(field => render(field, false))}
          </div>
        </div>
      )}

      {collapsible.length > 0 && (
        <Collapse
          ghost
          className={styles.advanced}
          // A section that already carries a value opens itself, so a saved
          // theme never hides settings behind a closed drawer.
          defaultActiveKey={collapsible
            .filter(section => section.set)
            .map(section => section.key)}>
          {/* antd 4 takes Panel children here; `items` is a v5 API. */}
          {collapsible.map(section => (
            <Collapse.Panel key={section.key} header={section.label}>
              {section.body}
            </Collapse.Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
};

export default FilterPanel;
